import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PROVIDERS,
  PROVIDER_LABELS,
  STORAGE_KEY,
  loadAddresses,
  type Provider,
  type RenderCheck,
  type Result,
} from "./emailOps/renderCheckTypes";
import { RenderCheckRow } from "./emailOps/RenderCheckRow";
import { RenderCheckEditDialog } from "./emailOps/RenderCheckEditDialog";

const AUTH_TEMPLATES = [
  { key: "auth:signup-verify", displayName: "Auth — signup verification" },
  { key: "auth:password-reset", displayName: "Auth — password reset" },
  { key: "auth:magic-link", displayName: "Auth — magic link (unused)" },
] as const;

type TemplateRowSummary = { templateName: string; displayName: string };

type Props = {
  templates: TemplateRowSummary[] | null;
};

export const EmailRenderCheckMatrix = ({ templates }: Props) => {
  const [addresses, setAddresses] = useState<Record<Provider, string>>(loadAddresses);
  const [checks, setChecks] = useState<RenderCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ template_key: string; provider: Provider } | null>(null);
  const [editResult, setEditResult] = useState<Result | "">("");
  const [editNotes, setEditNotes] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const rows = useMemo(() => {
    const transactional = (templates ?? []).map((t) => ({
      key: t.templateName,
      displayName: t.displayName,
      kind: "transactional" as const,
    }));
    const auth = AUTH_TEMPLATES.map((t) => ({
      key: t.key,
      displayName: t.displayName,
      kind: "auth" as const,
    }));
    return [...transactional, ...auth];
  }, [templates]);

  const persistAddresses = (next: Record<Provider, string>) => {
    setAddresses(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const loadChecks = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("email_render_check")
      .select("id, template_key, provider, result, notes, reviewed_at");
    if (error) {
      toast.error(error.message);
      setChecks([]);
    } else {
      setChecks((data ?? []) as RenderCheck[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    void loadChecks();
  }, []);

  const findCheck = (template_key: string, provider: Provider) =>
    checks.find((c) => c.template_key === template_key && c.provider === provider) ?? null;

  const sendTo = async (templateName: string, provider: Provider) => {
    const recipient = addresses[provider]?.trim();
    if (!recipient) {
      toast.error(`Set a ${PROVIDER_LABELS[provider]} address first.`);
      return;
    }
    const previewRow = templates?.find((t) => t.templateName === templateName);
    if (!previewRow) {
      toast.error("Template not loaded.");
      return;
    }
    const sendKey = `${templateName}:${provider}`;
    setSending(sendKey);
    try {
      const { data: previewData } = await supabase.functions.invoke("list-email-templates", { body: {} });
      const tplData = (previewData?.templates ?? []).find(
        (t: { templateName: string; previewData?: unknown }) => t.templateName === templateName,
      )?.previewData ?? {};
      const { error } = await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName,
          recipientEmail: recipient,
          idempotencyKey: `render-check-${templateName}-${provider}-${Date.now()}`,
          templateData: tplData,
        },
      });
      if (error) {
        toast.error(error.message ?? "Send failed.");
        return;
      }
      toast.success(`Sent ${templateName} to ${recipient}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Send failed.");
    } finally {
      setSending(null);
    }
  };

  const sendAllProviders = async (templateName: string) => {
    for (const p of PROVIDERS) {
      if (addresses[p]?.trim()) {
        await sendTo(templateName, p);
      }
    }
  };

  const openEdit = (template_key: string, provider: Provider) => {
    const existing = findCheck(template_key, provider);
    setEditing({ template_key, provider });
    setEditResult((existing?.result as Result | null) ?? "");
    setEditNotes(existing?.notes ?? "");
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    const { data: userData } = await supabase.auth.getUser();
    const payload = {
      template_key: editing.template_key,
      provider: editing.provider,
      result: editResult || null,
      notes: editNotes || null,
      reviewed_by: userData?.user?.id ?? null,
      reviewed_at: editResult ? new Date().toISOString() : null,
    };
    const { error } = await supabase
      .from("email_render_check")
      .upsert(payload, { onConflict: "template_key,provider" });
    setSavingEdit(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Saved.");
    setEditing(null);
    void loadChecks();
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="text-base">Render check matrix</CardTitle>
        <p className="text-sm text-muted-foreground">
          Save one test address per provider, send each template, then mark the result. Auth rows are tracked-only —
          trigger them from the live auth flow.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-3">
          {PROVIDERS.map((p) => (
            <div key={p} className="space-y-1">
              <Label htmlFor={`addr-${p}`}>{PROVIDER_LABELS[p]} address</Label>
              <Input
                id={`addr-${p}`}
                type="email"
                placeholder={`you+${p}@example.com`}
                value={addresses[p]}
                onChange={(e) => persistAddresses({ ...addresses, [p]: e.target.value })}
              />
            </div>
          ))}
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Template</th>
                {PROVIDERS.map((p) => (
                  <th key={p} className="px-3 py-2">{PROVIDER_LABELS[p]}</th>
                ))}
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <RenderCheckRow
                    key={row.key}
                    row={row}
                    findCheck={findCheck}
                    sending={sending}
                    onOpenEdit={openEdit}
                    onSendAll={(k) => void sendAllProviders(k)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>

      <RenderCheckEditDialog
        editing={editing}
        editResult={editResult}
        editNotes={editNotes}
        saving={savingEdit}
        onResultChange={setEditResult}
        onNotesChange={setEditNotes}
        onCancel={() => setEditing(null)}
        onSave={() => void saveEdit()}
      />
    </Card>
  );
};
